import { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useTable, useFilters, useSortBy } from "react-table";
import Switch from "@mui/material/Switch";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import IconButton from "@mui/material/IconButton";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MDTypography from "components/MDTypography";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

const formatData = (v) => (v ? String(v).substring(0, 16).replace("T", " ") : "");

const TABLE_KEY = "inventarios";
const LS_FILTERS_KEY = `tbl:${TABLE_KEY}:filters`;
const LS_SORT_KEY = `tbl:${TABLE_KEY}:sortBy`;
const LS_KEEP_KEY = `tbl:${TABLE_KEY}:keep`;

function filtroTextoAnd(rows, ids, filterValue) {
  const termos = String(filterValue || "")
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (termos.length === 0) return rows;

  return rows.filter((row) => {
    const texto = String(row.values[ids[0]] || "").toLowerCase();
    return termos.every((termo) => texto.includes(termo));
  });
}

filtroTextoAnd.autoRemove = (val) => !val;

function DefaultColumnFilter({ column }) {
  const { filterValue, setFilter } = column;
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      style={{
        width: "100%",
        padding: "2px 4px",
        fontSize: 11,
        border: "none",
        borderBottom: "1px solid #e0e0e0",
        outline: "none",
        backgroundColor: "transparent",
      }}
      placeholder="Filtrar..."
    />
  );
}

export default function Inventarios() {
  const { showAlert } = useAlert();
  const { userData } = useAuth();

  const [loading, setLoading] = useState(false);

  const [centros, setCentros] = useState([]);
  const [fabricantes, setFabricantes] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [inventarios, setInventarios] = useState([]);
  const [inventarioAberto, setInventarioAberto] = useState(null);
  const [materiaisInventario, setMateriaisInventario] = useState(null);

  const [centro, setCentro] = useState(null);
  const [fabricante, setFabricante] = useState(null);
  const [marca, setMarca] = useState(null);
  const [incluirEncerrados, setIncluirEncerrados] = useState(false);

  const idutilizador = Number(userData?.id || 0);

  const carregarCentros = async () => {
    const [data, err] = await handleApi(api.post("Stocks/SelInventarioCentrosCombo", {}));

    if (err) {
      showAlert(err, "error");
      return;
    }

    setCentros(
      Array.isArray(data)
        ? data.map((x) => ({
            ...x,
            refcentro: String(x.refcentro || "").trim(),
            centro: String(x.centro || x.refcentro || "").trim(),
          }))
        : []
    );
  };

  const carregarFabricantes = async () => {
    const [data, err] = await handleApi(api.post("Fabricantes/SelFabricantesCombo", {}));

    if (err) {
      showAlert(err, "error");
      return;
    }

    setFabricantes(Array.isArray(data) ? data : []);
  };

  const carregarMarcas = async (reffabricante) => {
    setMarcas([]);
    setMarca(null);

    if (!reffabricante) return;

    const [data, err] = await handleApi(
      api.post("Stocks/SelInventarioMarcasCombo", { reffabricante, term: "" })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    setMarcas(Array.isArray(data) ? data : []);
  };

  const carregarInventarios = async () => {
    if (!idutilizador) return;

    setLoading(true);

    const [data, err] = await handleApi(
      api.post("Stocks/SelInventarios", {
        idutilizador,
        incluirEncerrados: incluirEncerrados ? 1 : 0,
      })
    );

    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    setInventarios(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    if (!idutilizador) return;

    setLoading(true);

    Promise.all([carregarCentros(), carregarFabricantes(), carregarInventarios()]).finally(() =>
      setLoading(false)
    );
  }, [idutilizador]);

  useEffect(() => {
    carregarInventarios();
  }, [incluirEncerrados]);

  const criarInventario = async () => {
    if (!centro?.refcentro) {
      showAlert("Seleciona o centro.", "warning");
      return;
    }

    if (!fabricante?.ref) {
      showAlert("Seleciona o fabricante.", "warning");
      return;
    }

    if (!marca?.ref) {
      showAlert("Seleciona a marca.", "warning");
      return;
    }

    setLoading(true);

    const [data, err, meta] = await handleApi(
      api.post("Stocks/InsInventario", {
        refcentro: centro.refcentro,
        reffabricante: fabricante.ref,
        refmarca: marca.ref,
        idutilizadorabertura: idutilizador,
        obs: "",
      })
    );

    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    showAlert(meta?.message || "Inventário criado.", "success");
    await carregarInventarios();
  };

  const abrirInventario = async (row) => {
    setInventarioAberto(row);
    setLoading(true);

    const [data, err] = await handleApi(
      api.post("Stocks/SelInventarioMateriais", {
        idinventario: row.id,
        idutilizador,
      })
    );

    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    setMateriaisInventario(data || null);
  };

  const inventariosFiltrados = useMemo(() => {
    return inventarios.filter((x) => {
      if (centro?.refcentro && String(x.refcentro || "").trim() !== centro.refcentro) return false;
      if (fabricante?.ref && String(x.reffabricante || "").trim() !== fabricante.ref) return false;
      if (marca?.ref && String(x.refmarca || "").trim() !== marca.ref) return false;
      return true;
    });
  }, [inventarios, centro, fabricante, marca]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <PageHeader />

      <MDBox mt={2}>
        <Card>
          <MDBox p={2} display="grid" gap={2}>
            <MDBox
              display="grid"
              gap={2}
              sx={{
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
              }}
            >
              <Autocomplete
                options={centros}
                value={centro}
                onChange={(_, v) => {
                  setCentro(v);
                }}
                getOptionLabel={(o) =>
                  o?.centro ? `${o.centro} (${o.refcentro})` : o?.refcentro ? o.refcentro : ""
                }
                renderInput={(params) => <TextField {...params} label="Centro" size="small" />}
              />

              <Autocomplete
                options={fabricantes}
                value={fabricante}
                disabled={!centro}
                onChange={(_, v) => {
                  setFabricante(v);
                  carregarMarcas(v?.ref || "");
                }}
                getOptionLabel={(o) => (o?.nome ? `${o.nome} (${o.ref})` : "")}
                renderInput={(params) => <TextField {...params} label="Fabricante" size="small" />}
              />

              <Autocomplete
                options={marcas}
                value={marca}
                disabled={!fabricante}
                onChange={(_, v) => setMarca(v)}
                getOptionLabel={(o) => (o?.nome ? `${o.nome} (${o.ref})` : "")}
                renderInput={(params) => <TextField {...params} label="Marca" size="small" />}
              />
            </MDBox>

            <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
              <MDButton
                color="info"
                onClick={criarInventario}
                disabled={!centro || !fabricante || !marca}
              >
                Criar / Abrir Inventário
              </MDButton>

              <MDButton color="secondary" onClick={carregarInventarios}>
                Atualizar
              </MDButton>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={incluirEncerrados}
                    onChange={(e) => setIncluirEncerrados(e.target.checked)}
                    size="small"
                  />
                }
                label="Incluir encerrados"
              />
            </MDBox>
          </MDBox>
        </Card>

        <MDBox mt={2}>
          <Card>
            <MDBox p={2}>
              <MDTypography variant="h6">Inventários ({inventariosFiltrados.length})</MDTypography>

              <MDBox mt={2} sx={{ overflow: "auto", maxWidth: "100%" }}>
                <table style={{ width: "100%", minWidth: 900, borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {[
                        "",
                        "ID",
                        "Nome",
                        "Centro",
                        "Fabricante",
                        "Marca",
                        "Abertura",
                        "Encerramento",
                        "Aberto",
                      ].map((h) => (
                        <th key={h} style={thStyle}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {inventariosFiltrados.map((row) => (
                      <tr key={row.id}>
                        <td style={tdStyle}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => abrirInventario(row)}
                            title="Abrir"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </td>
                        <td style={tdStyle}>{row.id}</td>
                        <td style={tdStyle}>{row.nome}</td>
                        <td style={tdStyle}>{row.centro || row.refcentro}</td>
                        <td style={tdStyle}>{row.fabricante || row.reffabricante}</td>
                        <td style={tdStyle}>{row.marca || row.refmarca}</td>
                        <td style={tdStyle}>{formatData(row.dataabertura)}</td>
                        <td style={tdStyle}>{formatData(row.dataencerramento)}</td>
                        <td style={tdStyle}>{Number(row.aberto || 0) === 1 ? "Sim" : "Não"}</td>
                      </tr>
                    ))}

                    {inventariosFiltrados.length === 0 && (
                      <tr>
                        <td style={tdStyle} colSpan={9}>
                          Sem inventários para os filtros selecionados.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </MDBox>
            </MDBox>
          </Card>

          {inventarioAberto && materiaisInventario && (
            <MDBox mt={2}>
              <Card>
                <MDBox p={2}>
                  <MDTypography variant="h6">
                    Inventário aberto: {inventarioAberto.nome}
                  </MDTypography>

                  <MDBox mt={2}>
                    <MDTypography variant="subtitle2">
                      Por contar ({materiaisInventario.total_por_contar || 0})
                    </MDTypography>

                    <MDBox mt={1} sx={{ overflow: "auto", maxWidth: "100%" }}>
                      <table style={{ width: "100%", minWidth: 900, borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            {[
                              "Ref",
                              "Material",
                              "Unidade",
                              "Local Base",
                              "Locais Alternativos",
                            ].map((h) => (
                              <th key={h} style={thStyle}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(materiaisInventario.por_contar || []).map((row) => (
                            <tr key={row.refmaterial}>
                              <td style={tdStyle}>{row.refmaterial}</td>
                              <td style={tdStyle}>{row.material}</td>
                              <td style={tdStyle}>{row.unidade}</td>
                              <td style={tdStyle}>{row.local_base}</td>
                              <td style={tdStyle}>{row.locais_alt}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </MDBox>
                  </MDBox>

                  <MDBox mt={3}>
                    <MDTypography variant="subtitle2">
                      Contados ({materiaisInventario.total_contados || 0})
                    </MDTypography>

                    <MDBox mt={1} sx={{ overflow: "auto", maxWidth: "100%" }}>
                      <table style={{ width: "100%", minWidth: 1000, borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            {[
                              "Ref",
                              "Material",
                              "Unidade",
                              "Qtd verificada",
                              "Diferença",
                              "Utilizador",
                              "Data",
                            ].map((h) => (
                              <th key={h} style={thStyle}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(materiaisInventario.contados || []).map((row) => (
                            <tr key={row.refmaterial}>
                              <td style={tdStyle}>{row.refmaterial}</td>
                              <td style={tdStyle}>{row.material}</td>
                              <td style={tdStyle}>{row.unidade}</td>
                              <td style={tdStyle}>{row.qtd_verificada}</td>
                              <td style={tdStyle}>{row.diferenca}</td>
                              <td style={tdStyle}>{row.utilizador}</td>
                              <td style={tdStyle}>{formatData(row.data_registo)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          )}
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}

const thStyle = {
  fontSize: 12,
  textAlign: "left",
  padding: "8px",
  borderBottom: "1px solid #ddd",
  whiteSpace: "nowrap",
};

const tdStyle = {
  fontSize: 12,
  padding: "8px",
  borderBottom: "1px solid #eee",
  whiteSpace: "nowrap",
};
